import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 夜无疆"
        description="夜无疆是一个关于夜晚无限可能的探索平台。了解我们的品牌故事、核心价值和联系方式，加入夜行者社群。"
        keywords="关于夜无疆,夜无疆品牌,夜行者社群,联系我们"
        canonicalUrl="/about"
        schema={aboutSchema}
      />
      <main className="pt-20 bg-background-950">
        {/* Page Header */}
        <section className="py-16 md:py-20 bg-background-900">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-100 mb-4">关于夜无疆</h1>
            <p className="text-foreground-400 text-lg max-w-2xl leading-relaxed">
              打破黑夜边界，探索夜晚的无限可能。这里不仅是一个平台，更是一个属于所有夜行者的精神家园。
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <BrandIntro />

        {/* Reputation / Testimonials */}
        <ReputationSection />

        {/* FAQ */}
        <FAQSection />

        {/* Contact */}
        <ContactSection />
      </main>
    </>
  );
}

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于夜无疆',
    description: '夜无疆 - 打破黑夜边界的极致体验。关于夜晚无限可能的探索平台。',
    url: 'https://wwyjs.cn/about',
  },
];

function BrandIntro() {
  return (
    <section className="py-16 md:py-24 bg-background-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Story */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 mb-20">
          <div className="lg:w-1/2">
            <div className="rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20night%20scene%20with%20silhouette%20of%20person%20standing%20on%20hilltop%20overlooking%20distant%20city%20lights%2C%20starry%20sky%2C%20cinematic%20moody%20composition%2C%20deep%20shadows%20with%20warm%20golden%20light%20on%20horizon%2C%20contemplative%20nocturnal%20landscape&width=700&height=500&seq=about-story&orientation=landscape"
                alt="夜无疆品牌故事"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:w-1/2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-400 text-sm mb-4">
              <i className="ri-book-open-line"></i> 品牌故事
            </span>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-5">
              从一个人到一群人，夜无疆的故事
            </h2>
            <div className="space-y-4 text-foreground-400 leading-relaxed">
              <p>
                夜无疆诞生于一个简单的想法：夜晚不应该被浪费。创始人是一位长期夜间创作者，他发现夜晚有着白天无法比拟的宁静与力量，于是决定建立一个属于所有夜行者的家园。
              </p>
              <p>
                从最初的个人博客到如今覆盖数万夜行者的探索平台，夜无疆始终坚持一个核心理念：<strong className="text-foreground-200">打破黑夜的边界，让每一个夜晚都充满可能。</strong>
              </p>
              <p>
                无论是深夜创作者、夜间生活爱好者，还是只是想找一处安静角落的独行者——在夜无疆，你总能找到属于自己的那片夜空。
              </p>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div>
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-3">我们的核心理念</h2>
            <p className="text-foreground-500">支撑夜无疆走到今天的四个信念</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((value) => (
              <div key={value.title} className="p-6 rounded-xl bg-background-900 border border-background-800/50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-500/10">
                  <i className={`${value.icon} text-primary-400 text-xl`}></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-200 mb-2">{value.title}</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const values = [
  {
    icon: 'ri-moon-clear-line',
    title: '拥抱夜晚',
    description: '夜晚不是白天的延续，而是一个完全不同的世界。我们鼓励以全新的视角去体验夜晚的每一个时刻。',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: '激发创造',
    description: '在静谧的深夜，灵感最容易浮现。我们为创作者提供灵感的环境和社群支持，让创意自由生长。',
  },
  {
    icon: 'ri-empathize-line',
    title: '情感共鸣',
    description: '每一个深夜都有故事，每一种情绪都值得被看见。我们相信，分享与连接是最治愈的力量。',
  },
  {
    icon: 'ri-group-2-line',
    title: '社群连接',
    description: '夜行者不孤单。我们致力于连接每一个热爱夜晚的人，让深夜的探索之旅充满陪伴与温暖。',
  },
];

function ReputationSection() {
  const testimonials = [
    {
      quote: '夜无疆让我重新认识了夜晚。以前总觉得夜晚是孤独的，现在发现夜晚可以如此丰富和充满力量。',
      author: '深夜创作者',
      role: '夜无疆签约作者',
      avatar: 'https://readdy.ai/api/search-image?query=Dark%20minimal%20portrait%20silhouette%20with%20warm%20amber%20rim%20lighting%2C%20professional%20moody%20profile%2C%20deep%20black%20background&width=100&height=100&seq=testimonial-1&orientation=squarish',
    },
    {
      quote: '加入夜无疆社群是我今年最正确的决定。在这里找到了一群理解夜晚、热爱夜晚的伙伴。',
      author: '城市夜行者',
      role: '社群活跃成员',
      avatar: 'https://readdy.ai/api/search-image?query=Dark%20minimal%20portrait%20silhouette%20with%20warm%20golden%20rim%20lighting%2C%20friendly%20moody%20profile%2C%20deep%20black%20background&width=100&height=100&seq=testimonial-2&orientation=squarish',
    },
    {
      quote: '作为一个长期失眠的人，夜无疆的文章给了我很多安慰。深夜读一篇好文，是最好的疗愈。',
      author: '深夜读者',
      role: '忠实读者',
      avatar: 'https://readdy.ai/api/search-image?query=Dark%20minimal%20portrait%20silhouette%20with%20warm%20bronze%20rim%20lighting%2C%20calm%20moody%20profile%2C%20deep%20black%20background&width=100&height=100&seq=testimonial-3&orientation=squarish',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-background-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/10 text-accent-400 text-sm mb-4">
            <i className="ri-chat-quote-line"></i> 夜行者之声
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-3">听听夜行者们怎么说</h2>
          <p className="text-foreground-500">来自社群成员的真实反馈</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <div key={t.author} className="p-6 rounded-xl bg-background-950 border border-background-800/50">
              <div className="mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-primary-400 text-sm mr-0.5"></i>
                ))}
              </div>
              <p className="text-foreground-400 text-sm leading-relaxed mb-5 italic">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={t.author}
                  className="w-10 h-10 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <div className="text-foreground-300 text-sm font-medium">{t.author}</div>
                  <div className="text-foreground-600 text-xs">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    {
      question: '夜无疆是什么？',
      answer: '夜无疆是一个关于夜晚无限可能的探索平台，涵盖夜间生活、创作灵感、深夜情绪和夜行者社群等多元内容。我们致力于打破夜晚的边界，让更多人发现并享受夜晚的美好。',
    },
    {
      question: '如何加入夜行者社群？',
      answer: '你可以通过订阅夜无疆通讯来接收最新文章和社群活动信息。我们也会定期组织线下夜间主题活动，欢迎每一位热爱夜晚的夜行者参与。',
    },
    {
      question: '夜无疆的内容更新频率如何？',
      answer: '我们每周发布3-5篇原创深度文章，涵盖夜间生活、创作灵感和深夜情绪等多个主题。周一至周五每天都有新内容更新。',
    },
    {
      question: '可以在夜无疆投稿吗？',
      answer: '我们非常欢迎夜行者们的投稿！如果你有关于夜晚的故事、创作或感悟，欢迎通过联系表单与我们沟通。我们的编辑团队会认真阅读每一份投稿。',
    },
    {
      question: '夜无疆是免费的吗？',
      answer: '完全免费！夜无疆的所有文章和社群活动都对所有夜行者开放。我们相信，夜晚的美好应该被所有人共享。',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section className="py-16 md:py-24 bg-background-950">
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-3">常见问题</h2>
          <p className="text-foreground-500">关于夜无疆，你可能会想知道这些</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq) => (
            <FAQItem key={faq.question} faq={faq} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ faq }: { faq: { question: string; answer: string } }) {
  const [open, setOpen] = useState(false);
  const iconClass = open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line';

  return (
    <div className="rounded-xl bg-background-900 border border-background-800/50 overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium text-foreground-300 text-sm pr-4">{faq.question}</span>
        <i className={`${iconClass} text-foreground-500 transition-transform duration-200 text-lg`}></i>
      </button>
      <div
        className={`transition-all duration-300 overflow-hidden ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="px-5 pb-5 text-foreground-500 text-sm leading-relaxed">{faq.answer}</p>
      </div>
    </div>
  );
}

function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [serverMessage, setServerMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');

    const form = e.currentTarget;
    const formDataObj = new FormData(form);

    const honeypot = formDataObj.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      setServerMessage('消息已发送！我们会尽快回复你。');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    const payload = new URLSearchParams();
    formDataObj.forEach((value, key) => {
      if (key !== 'phone_alt') {
        payload.append(key, String(value));
      }
    });

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setServerMessage('消息已发送！我们会尽快回复你。');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
        setServerMessage(parsed?.meta?.message || parsed?.message || '发送失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setServerMessage('网络错误，请稍后重试');
    }
  };

  return (
    <section className="py-16 md:py-24 bg-background-900">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-500/10 text-secondary-400 text-sm mb-4">
            <i className="ri-mail-send-line"></i> 联系我们
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-3">与我们取得联系</h2>
          <p className="text-foreground-500">有任何问题、建议或合作意向，欢迎随时联系我们</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Contact Info */}
          <div className="lg:w-1/3 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                <i className="ri-mail-line text-primary-400"></i>
              </div>
              <div>
                <h4 className="text-foreground-300 font-medium text-sm mb-1">邮箱</h4>
                <p className="text-foreground-500 text-sm">hello@wwyjs.cn</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                <i className="ri-map-pin-line text-primary-400"></i>
              </div>
              <div>
                <h4 className="text-foreground-300 font-medium text-sm mb-1">地址</h4>
                <p className="text-foreground-500 text-sm">中国上海市静安区南京西路1788号</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                <i className="ri-time-line text-primary-400"></i>
              </div>
              <div>
                <h4 className="text-foreground-300 font-medium text-sm mb-1">工作时间</h4>
                <p className="text-foreground-500 text-sm">周一至周五 14:00 - 02:00</p>
                <p className="text-foreground-600 text-xs mt-0.5">（没错，我们也是夜行者）</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:w-2/3">
            {status === 'success' ? (
              <div className="bg-primary-500/10 border border-primary-500/20 rounded-xl p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/20">
                  <i className="ri-check-line text-primary-400 text-2xl"></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-200 mb-2">消息已发送！</h3>
                <p className="text-foreground-400 text-sm">{serverMessage}</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                action="https://readdy.ai/api/form/d9u074n8l97n3eas3es0"
                method="POST"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="website-field-wrap">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-400 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background-950 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-all duration-200"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-400 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background-950 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-all duration-200"
                      placeholder="你的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm text-foreground-400 mb-1.5">主题</label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background-950 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-all duration-200"
                    placeholder="消息主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-400 mb-1.5">消息内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background-950 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-all duration-200 resize-none"
                    placeholder="请输入你想说的内容（最多500字）"
                  />
                  <p className="text-foreground-600 text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>
                {status === 'error' && (
                  <p className="text-red-400 text-sm">{serverMessage}</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-950 font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
                >
                  {status === 'loading' ? '发送中...' : '发送消息'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}